import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Trash2, ShoppingBag, Check, Building2, Tag, X, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useCart, useSettings } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const toCoverUrl = (url) => {
  if (!url) return null;
  const m = url.match(/\/proxy-image\/([a-zA-Z0-9_-]+)/) ||
            url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
            url.match(/\/d\/([a-zA-Z0-9_-]+)/) ||
            url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m && BACKEND_URL) return `${BACKEND_URL}/api/proxy-image/${m[1]}`;
  return url;
};

// Format label by product type (audiobook never says PDF; premium shows both)
const FORMAT = { ebook: 'E-book (PDF)', audiobook: 'Audiobook (MP3)', premium: 'E-book (PDF) + Audiobook (MP3)' };
const formatLabel = (t) => FORMAT[t] || 'E-book (PDF)';
const baseName = (name) => (name || '').replace(/\s*[—-]\s*(Audiobook|Premium)\s*$/i, '');

const CheckoutPage = () => {
  const { cart, removeFromCart, cartTotal, addToCart } = useCart();
  const icFired = useRef(false);
  useEffect(() => {
    // Meta Pixel — InitiateCheckout, once when the checkout has items (consent-gated)
    if (!icFired.current && cartTotal > 0 && window.fbq) {
      icFired.current = true;
      window.fbq('track', 'InitiateCheckout', { value: cartTotal, currency: 'PLN' });
    }
  }, [cartTotal]);
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  const checkoutToken = searchParams.get('token');
  const cartToken = searchParams.get('cart');
  const [tokenLoading, setTokenLoading] = useState(!!(checkoutToken || cartToken));

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    is_company: false,
    company_name: '',
    nip: '',
    company_address: '',
    invoice_notes: '',
    payment_method: 'blik',
    acceptTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [pendingPromo, setPendingPromo] = useState(null); // promo restored from abandoned cart, applied once cart is ready

  useEffect(() => {
    if (!checkoutToken) return;
    axios.get(`${API}/custom-checkouts/${checkoutToken}`)
      .then(res => {
        const { products, email, name } = res.data;
        products.forEach(p => addToCart(p));
        if (email || name) {
          setFormData(prev => ({
            ...prev,
            email: email || prev.email,
            first_name: name || prev.first_name,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setTokenLoading(false));
  }, [checkoutToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cartToken) return;
    axios.get(`${API}/abandoned-carts/restore/${cartToken}`)
      .then(res => {
        const { products, email, first_name, promo_code } = res.data;
        products.forEach(p => addToCart(p));
        setFormData(prev => ({
          ...prev,
          email: email || prev.email,
          first_name: first_name || prev.first_name,
        }));
        if (promo_code) { setPromoCode(promo_code); setPendingPromo(promo_code); }
      })
      .catch(() => {})
      .finally(() => setTokenLoading(false));
  }, [cartToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply a promo restored from an abandoned cart, once the cart (and total) is ready
  useEffect(() => {
    if (!pendingPromo || appliedPromo || cart.length === 0 || cartTotal <= 0) return;
    const code = pendingPromo;
    setPendingPromo(null); // consume immediately to avoid re-runs
    axios.post(`${API}/promo-codes/validate?code=${code}&order_total=${cartTotal}`)
      .then(res => { setAppliedPromo(res.data); setPromoCode(''); })
      .catch(() => { setPromoCode(code); }); // invalid/expired — leave it in the input for manual retry
  }, [pendingPromo, appliedPromo, cart.length, cartTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');

    try {
      const response = await axios.post(`${API}/promo-codes/validate?code=${promoCode}&order_total=${cartTotal}`);
      setAppliedPromo(response.data);
      setPromoCode('');
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Nieprawidłowy kod promocyjny');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError('');
  };

  const handleDismissCancelled = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('cancelled');
    setSearchParams(params, { replace: true });
  };

  // Calculate final total
  const finalTotal = appliedPromo ? appliedPromo.new_total : cartTotal;
  const STRIPE_MIN = 3.0; // Stripe min €0.50; EUR-settling account needs a PLN floor safely above it across FX swings
  const chargedTotal = finalTotal > 0 && finalTotal < STRIPE_MIN ? STRIPE_MIN : finalTotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptTerms) {
      setError('Musisz zaakceptować regulamin');
      return;
    }
    if (cart.length === 0) {
      setError('Koszyk jest pusty');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Persist the abandoned cart AND create the Stripe session in parallel, then wait
      // for BOTH before navigating away. Previously the cart save was fire-and-forget and
      // the redirect to Stripe aborted the in-flight request, so carts were never saved
      // (→ no reminder emails). Running them together keeps checkout just as fast.
      const [, response] = await Promise.all([
        axios.post(`${API}/abandoned-carts`, {
          email: formData.email,
          first_name: formData.first_name,
          products: cart.map(i => ({ id: i.id, name: i.name, price: i.is_on_sale ? i.sale_price : i.regular_price })),
          total: finalTotal,
          promo_code: appliedPromo?.code || null,
        }).catch(() => {}),
        axios.post(`${API}/checkout/create-session`, {
          items: cart.map(item => ({ product_id: item.id, quantity: item.quantity })),
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          is_company: formData.is_company,
          company_name: formData.company_name,
          nip: formData.nip,
          company_address: formData.company_address,
          invoice_notes: formData.invoice_notes,
          payment_method: formData.payment_method,
          promo_code: appliedPromo?.code || null,
          origin_url: window.location.origin,
          accept_terms: formData.acceptTerms
        }),
      ]);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error('Checkout error:', detail || err.message);
      setError('Wystąpił błąd. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  if (tokenLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#6B7280] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Twój koszyk jest pusty</h2>
          <p className="text-[#9CA3AF] mb-6">Dodaj produkty do koszyka, aby kontynuować</p>
          <Link to="/" className="btn-primary">Przeglądaj produkty</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-white/5 py-4">
        <div className="container-custom flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">
            <span className="text-white">{settings.site_name?.replace(/([A-Z])/g, ' $1').trim().split(' ').slice(0, -1).join('')}</span>
            <span className="text-[#10b981]">{settings.site_name?.replace(/([A-Z])/g, ' $1').trim().split(' ').slice(-1)[0]}.</span>
          </Link>
          <div className="flex items-center gap-2 text-[#6B7280] text-sm">
            <Lock size={16} />
            Bezpieczne połączenie SSL
          </div>
        </div>
      </header>

      {cancelled && (
        <div className="relative bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 px-10 py-3 text-center">
          Płatność została anulowana. Możesz spróbować ponownie.
          <button
            type="button"
            onClick={handleDismissCancelled}
            aria-label="Zamknij"
            className="absolute right-5 sm:right-8 lg:right-12 top-1/2 -translate-y-1/2 text-yellow-400/70 hover:text-yellow-300 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <main className="container-custom py-8 md:py-12 flex-1">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - Order Summary */}
          <div className="glass-card p-6" data-testid="order-summary">
            <h2 className="text-xl font-bold text-white mb-6">Podsumowanie</h2>
            
            <div className="space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4 p-4 bg-[#050505] rounded-xl border border-white/5" data-testid={`cart-item-${item.id}`}>
                  <div className="w-20 h-24 sm:w-24 sm:h-28 bg-[#10b981]/10 rounded-lg flex items-center justify-center border border-[#10b981]/30 flex-shrink-0 p-1">
                    {item.cover_image ? (
                      <img src={toCoverUrl(item.cover_image)} alt={item.name} className="w-full h-full object-contain rounded-md" />
                    ) : (
                      <ShoppingBag className="text-[#10b981]" size={24} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        {item.badge && (
                          <span className="inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-[#10b981]/15 border border-[#10b981]/40 text-[#10b981] mb-1.5">{item.badge}</span>
                        )}
                        <h3 className="text-white font-medium">{baseName(item.name)}</h3>
                        {item.subtitle && <p className="text-[#6B7280] text-xs mt-0.5">{item.subtitle}</p>}
                        <p className="text-[#9CA3AF] text-sm mt-1">{formatLabel(item.product_type)}</p>
                      </div>
                      <button onClick={() => removeFromCart(item.id)} className="text-[#6B7280] hover:text-red-400 transition-colors" data-testid={`remove-item-${item.id}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <p className="text-[#10b981] font-bold mt-2">
                      {Number(item.is_on_sale ? item.sale_price : item.regular_price) || 0} zł
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/5 pt-4">
              {/* Promo Code Input */}
              <div className="mb-4">
                {appliedPromo ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag size={16} className="text-emerald-400" />
                      <span className="text-emerald-400 font-medium">{appliedPromo.code}</span>
                      <span className="text-zinc-400 text-sm">
                        (-{appliedPromo.discount_type === 'percentage' ? `${appliedPromo.discount_value}%` : `${appliedPromo.discount_amount} zł`})
                      </span>
                    </div>
                    <button 
                      onClick={handleRemovePromo}
                      className="text-zinc-400 hover:text-red-400"
                      data-testid="remove-promo-btn"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Kod promocyjny"
                      className="flex-1 px-4 py-2 bg-[#050505] border border-white/10 rounded-lg text-white placeholder-zinc-500 text-sm focus:border-emerald-500 focus:outline-none"
                      data-testid="promo-code-input"
                    />
                    <button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 disabled:opacity-50 text-sm transition-colors"
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? '...' : 'Zastosuj'}
                    </button>
                  </div>
                )}
                {promoError && <p className="text-red-400 text-sm mt-2">{promoError}</p>}
              </div>

              {/* Totals */}
              {appliedPromo && (
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-zinc-400">Wartość zamówienia:</span>
                  <span className="text-zinc-400 line-through">{cartTotal.toFixed(2)} zł</span>
                </div>
              )}
              {appliedPromo && (
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-emerald-400">Rabat:</span>
                  <span className="text-emerald-400">-{appliedPromo.discount_amount.toFixed(2)} zł</span>
                </div>
              )}
              <div className="flex justify-between items-center text-lg">
                <span className="text-[#9CA3AF]">Razem:</span>
                <span className="text-2xl font-bold text-[#10b981]" data-testid="cart-total">{finalTotal.toFixed(2)} zł</span>
              </div>
              <p className="text-[#6B7280] text-sm mt-1">Jednorazowa płatność • Dostęp natychmiast</p>
              {finalTotal > 0 && finalTotal < STRIPE_MIN && (
                <p className="text-amber-400 text-xs mt-2">
                  Stripe wymaga minimalnej kwoty {STRIPE_MIN.toFixed(2)} zł — zostanie pobrane {STRIPE_MIN.toFixed(2)} zł zamiast {finalTotal.toFixed(2)} zł.
                </p>
              )}
            </div>

            {/* Benefits */}
            <div className="mt-6 p-4 bg-[#050505] rounded-xl border border-white/5">
              <h4 className="text-white font-medium mb-3">CO OTRZYMUJESZ:</h4>
              <div className="space-y-2 text-sm">
                {cart.map(item => (
                  <div key={item.id} className="flex items-start gap-2">
                    <Check size={16} className="text-[#10b981] mt-0.5 flex-shrink-0" />
                    <span className="text-[#9CA3AF]">
                      {baseName(item.name)}
                      <span className="block text-[#6B7280] text-xs">{formatLabel(item.product_type)}</span>
                    </span>
                  </div>
                ))}
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-[#10b981] mt-0.5" />
                  <span className="text-[#9CA3AF]">Bonusy wdrożeniowe gratis</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-[#10b981] mt-0.5" />
                  <span className="text-[#9CA3AF]">Dożywotni dostęp i aktualizacje</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check size={16} className="text-[#10b981] mt-0.5" />
                  <span className="text-[#9CA3AF]">Natychmiastowa wysyłka na e-mail</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="glass-card p-6" data-testid="checkout-form">
            <h2 className="text-xl font-bold text-white mb-6">Twoje dane</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Adres e-mail *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="twoj@email.com" className="w-full" required data-testid="input-email" />
                  <p className="text-xs text-[#6B7280] mt-1">Na ten adres wyślemy dostęp do materiałów.</p>
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Imię *</label>
                  <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Np. Jan" className="w-full" required data-testid="input-name" />
                </div>
              </div>

              {/* Company section */}
              <div className="border border-white/5 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" name="is_company" checked={formData.is_company} onChange={handleChange} className="checkbox-custom" data-testid="checkbox-company" />
                  <div className="flex items-center gap-2">
                    <Building2 size={18} className="text-[#9CA3AF]" />
                    <span className="text-white">Kupuję na firmę (chcę fakturę)</span>
                  </div>
                </label>

                {formData.is_company && (
                  <div className="mt-4 space-y-4 animate-fade-in-up">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-[#9CA3AF] mb-2">Nazwa firmy *</label>
                        <input type="text" name="company_name" value={formData.company_name} onChange={handleChange} className="w-full" required={formData.is_company} />
                      </div>
                      <div>
                        <label className="block text-sm text-[#9CA3AF] mb-2">NIP *</label>
                        <input type="text" name="nip" value={formData.nip} onChange={handleChange} placeholder="1234567890" className="w-full" required={formData.is_company} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-[#9CA3AF] mb-2">Adres firmy *</label>
                      <input type="text" name="company_address" value={formData.company_address} onChange={handleChange} placeholder="Ulica, kod pocztowy, miasto" className="w-full" required={formData.is_company} />
                    </div>
                    <div>
                      <label className="block text-sm text-[#9CA3AF] mb-2">Uwagi do faktury (opcjonalnie)</label>
                      <input type="text" name="invoice_notes" value={formData.invoice_notes} onChange={handleChange} placeholder="np. numer zamówienia" className="w-full" />
                    </div>
                    <p className="text-xs text-[#6B7280]">Po zakupie wyślemy fakturę na podany e-mail.</p>
                  </div>
                )}
              </div>

              {/* Payment method — chosen on Stripe's secure page in the next step */}
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-3">Metoda płatności (Stripe)</label>
                <div className="p-4 rounded-xl border border-white/10 bg-[#050505]">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock size={18} className="text-[#10b981] flex-shrink-0" />
                    <p className="text-white font-medium text-sm">Metodę wybierzesz w następnym kroku</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-black border border-white/15 text-white text-xs font-bold tracking-wide">BLIK</span>
                    <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-white/5 border border-white/10 text-[#e5e7eb] text-xs font-medium"><CreditCard size={14} /> Karta</span>
                    <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-white border border-white/10 text-xs font-extrabold"><span style={{ color: '#d13239' }}>Przelewy</span><span style={{ color: '#1a1a1a' }}>24</span></span>
                    <span className="text-xs text-[#6B7280]">i inne</span>
                  </div>
                </div>
                <p className="text-xs text-[#6B7280] mt-2">Bezpieczne płatności obsługuje: <span className="text-white">Stripe</span></p>
              </div>

              {/* Terms */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} className="checkbox-custom mt-1" data-testid="checkbox-terms" />
                <span className="text-sm text-[#9CA3AF]">
                  Akceptuję <Link to="/dokument/regulamin" className="text-[#10b981] hover:underline">Regulamin</Link> i <Link to="/dokument/polityka-prywatnosci" className="text-[#10b981] hover:underline">Politykę Prywatności</Link> serwisu. Rozumiem, że kupuję treść cyfrową i wyrażam zgodę na natychmiastowe rozpoczęcie realizacji zamówienia oraz przyjmuję do wiadomości, że w związku z tym tracę prawo do odstąpienia od umowy.
                </span>
              </label>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-base sm:text-lg whitespace-nowrap" data-testid="submit-checkout">
                {loading ? <div className="spinner"></div> : `Zamawiam i płacę — ${chargedTotal.toFixed(2)} zł →`}
              </button>

              <p className="text-center text-xs text-[#6B7280]">Dostęp natychmiast po zaksięgowaniu płatności</p>
            </form>

            {/* Trust bar */}
            <div className="mt-6 p-4 bg-[#050505] rounded-xl border border-white/5">
              <p className="text-center text-sm text-[#6B7280] mb-3">GWARANTUJEMY BEZPIECZEŃSTWO</p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-[#9CA3AF]">
                <span>✓ Bezpieczna płatność</span>
                <span>✓ Dostęp natychmiast</span>
                <span>✓ Gwarancja satysfakcji</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="container-custom flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#6B7280]">
          <p>© {new Date().getFullYear()} {settings.site_name}. Wszelkie prawa zastrzeżone.</p>
          <div className="flex gap-6">
            <Link to="/dokument/regulamin" className="hover:text-[#10b981]">Regulamin</Link>
            <Link to="/dokument/polityka-prywatnosci" className="hover:text-[#10b981]">Polityka Prywatności</Link>
            <Link to="/kontakt" className="hover:text-[#10b981]">Kontakt</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutPage;